import React, { useContext, useEffect, useRef } from 'react';
import {
    Table,
    Input,
    Button,
    Popconfirm,
    Form,
    Space,
    Row,
    Col,
    Pagination,
    Typography,
    notification,
    Radio,
    InputNumber,
    Modal,
} from 'antd';
import { FormInstance } from 'antd/lib/form';
import {
    PlusOutlined,
    MinusCircleOutlined,
    SaveOutlined,
} from '@ant-design/icons';
import { PrimaryTooltip } from 'components/index';
import API_SERVICE from 'services/api-service';
import { withRouter } from 'react-router';
import { CommonLinkProjects } from 'views/Claims/CommonLinkProjects';
import { directCostModalContent, indirectCostModalContent } from '../directCostGuidance';

const { Text } = Typography;
const EditableContext = React.createContext<FormInstance<any> | null>(null);

interface Item {
    key: string;
    name: string;
    age: string;
    address: string;
    isDirectCost: boolean;
}

interface EditableRowProps {
    index: number;
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};

interface EditableCellProps {
    title: React.ReactNode;
    editable: boolean;
    children: React.ReactNode;
    dataIndex: keyof Item;
    record: Item;
    handleSave: (record: Item) => void;
    inputType: 'number' | 'text' | 'select' | 'radio' | 'percentage';
    claimDetails?: any;
}

const EditableCell: React.FC<EditableCellProps> = ({
    title,
    editable,
    children,
    dataIndex,
    record,
    handleSave,
    inputType,
    claimDetails,
    ...restProps
}) => {
    const inputRef = useRef<any>(null);
    const form = useContext(EditableContext)!;

    //@ts-ignore
    useEffect(() => {
        form.setFieldsValue({ [dataIndex]: record?.[dataIndex] });
    }, [record?.[dataIndex]]);

    /*useEffect(() => {
        if (editing) {
            inputRef.current!.focus();
        }
    }, [editing]);

    const toggleEdit = () => {
        setEditing(!editing);
        form.setFieldsValue({ [dataIndex]: record[dataIndex] });
    };*/

    const handleIsConnectedChange = async (event: any) => {
        if (event.target.value) {
            form.setFieldsValue({ adjustmentsPercentage: 100 });
        } else {
            form.setFieldsValue({ adjustmentsPercentage: 65 });
        }
        save();
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            const rndPercentage = parseFloat(values.rndAllocationPercentage) || 0;
            const netAmount = parseFloat(values.netAmount) || 0;
            const rndTotal = (rndPercentage / 100) * netAmount;
            const adjustmentsPercentage = values.isConnected ? 100 : 65;
            const total = (adjustmentsPercentage / 100) * rndTotal;

            values.total = total;
            values.rndTotal = rndTotal;
            values.adjustmentsPercentage = adjustmentsPercentage;

            // Calculate SME and RDEC values based on claim type
            const claimTypeId = claimDetails?.typeId;
            if (claimTypeId === 3) { // TYPE_MIXED
                values.smeTotal = (values.smeAllocation / 100) * values.rndTotal;
                values.rdecAllocation = 100 - values.smeAllocation;
                values.rdecTotal = (values.rdecAllocation / 100) * values.rndTotal;
            } else if (claimTypeId === 2) { // TYPE_SME
                values.smeAllocation = 100;
                values.smeTotal = values.rndTotal;
                values.rdecAllocation = 0;
                values.rdecTotal = 0;
            } else if (claimTypeId === 1) { // TYPE_RDEC
                values.rdecAllocation = 100;
                values.rdecTotal = values.rndTotal;
                values.smeAllocation = 0;
                values.smeTotal = 0;
            } else { // TYPE_MERGED or other types
                values.smeTotal = 0;
                values.rdecAllocation = 0;
                values.rdecTotal = 0;
                values.smeAllocation = 0;
            }

            // toggleEdit();
            handleSave({ ...record, ...values });
        } catch (errInfo) {
            console.log('Save failed:', errInfo);
        }
    };

    let childNode = children;

    if (editable) {
        childNode = (
            <Form.Item
                style={{ margin: 0 }}
                name={dataIndex}
                rules={[
                    {
                        required: true,
                        message: `Field required.`,
                    },
                ]}
            >
                {inputType === 'radio' ? (
                    <Radio.Group
                        onChange={(e) => {
                            if (dataIndex === 'isDirectCost') {
                                Modal.info({
                                    title: e.target.value
                                        ? 'Direct Cost Guidance'
                                        : 'Indirect Qualifying Activities',
                                    content: e.target.value
                                        ? directCostModalContent
                                        : indirectCostModalContent,
                                    centered: true,
                                    width: 720,
                                });
                            }
                            handleIsConnectedChange(e)
                        }}
                        ref={inputRef}
                    >
                        <Radio value={true}>Yes</Radio>
                        <Radio value={false}>No</Radio>
                    </Radio.Group>
                ) : inputType === 'number' ? (
                    <InputNumber
                        formatter={(value) =>
                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        //@ts-ignore
                        parser={(value) => value.replace(/\£\s?|(,*)/g, '')}
                        //@ts-ignore
                        addonBefore={'£'}
                        ref={inputRef}
                        onPressEnter={save}
                        onBlur={() => setTimeout(save, 0)}
                    />
                ) : inputType === 'percentage' ? (
                    <InputNumber
                        min={0}
                        max={100}
                        formatter={(value) => `${value}`}
                        //@ts-ignore
                        parser={(value) => value.replace('%', '')}
                        //@ts-ignore
                        addonAfter={'%'}
                        ref={inputRef}
                        onPressEnter={save}
                        onBlur={() => setTimeout(save, 0)}
                    />
                ) : (
                    <Input ref={inputRef} onPressEnter={save} onBlur={() => setTimeout(save, 0)} />
                )}
            </Form.Item>
        );
    }

    return <td {...restProps}>{childNode}</td>;
};

type EditableTableProps = Parameters<typeof Table>[0];

interface DataType {
    key: React.Key;
    id: null;
    name: string;
    role: string;
    agency: string;
    netAmount: string;
    rndAllocationPercentage: string;
    isConnected: boolean;
    adjustmentsPercentage: number;
    isDirectCost: boolean | null;
    projects: any[];
}

interface EditableTableState {
    dataSource: DataType[];
    count: number;
    fetching: boolean;
    total: number;
    pagination: { page: number; perpage: any };
    financialDetails: Array<any>;
    claimId: any;
    updateData: Array<any>;
    saving: boolean;
    externalWorkerCostTotal: {
        netAmount: number;
        rnd: number;
        externalWorkerAdjustment: number;
        total: number;
    };
    claimDetails: any;
    columns: any;
    compareCount: number;
    openLinkProjectsModal: boolean;
    record: any;
    index: number | null;
    projectsList: any[];
}

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>;

class ExternalWorkers extends React.Component<
    EditableTableProps,
    EditableTableState
> {
    columns: (ColumnTypes[number] & {
        editable?: boolean;
        dataIndex: string;
    })[];

    constructor(props: EditableTableProps) {
        super(props);

        this.columns = [
            {
                title: '',
                dataIndex: 'del',
                width: '50px',
                //@ts-ignore
                render: (_, record: { key: React.Key }) =>
                    this.state.dataSource.length >= 1 ? (
                        <Popconfirm
                            title="Sure to delete?"
                            onConfirm={() => this.handleDelete(record)}
                        >
                            <div className="text-primary cursor-pointer text-center">
                                <MinusCircleOutlined />
                            </div>
                        </Popconfirm>
                    ) : null,
            },
            {
                title: 'S.No',
                dataIndex: 'sNO',
                width: '70px',
                render: (a: any, v: any, index: number) => (
                    <Text>
                        {(this.state.pagination.page - 1) *
                            this.state.pagination.perpage +
                            index +
                            1}
                    </Text>
                ),
            },
            {
                title: (
                    <PrimaryTooltip
                        title="External Worker Name"
                        text="External Worker Name"
                    />
                ),
                dataIndex: 'name',
                width: '200px',
                editable: true,
            },
            {
                title: <PrimaryTooltip title="Role" text="Role" />,
                dataIndex: 'role',
                width: '180px',
                editable: true,
            },
            {
                title: <PrimaryTooltip title="Agency" text="Agency" />,
                dataIndex: 'agency',
                width: '180px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="Net Amount (GBP)"
                        text="Net Amount (GBP)"
                    />
                ),
                dataIndex: 'netAmount',
                width: '300px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="R&D Allocation (%)"
                        text="R&D Allocation (%)"
                    />
                ),
                dataIndex: 'rndAllocationPercentage',
                width: '220px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="Is Connected?"
                        text="Is Connected?"
                    />
                ),
                dataIndex: 'isConnected',
                width: '150px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="External Worker Adjustment (GBP)"
                        text="Worker Adjustment (%)"
                    />
                ),
                dataIndex: 'adjustmentsPercentage',
                width: '260px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip title="Total (GBP)" text="Total (GBP)" />
                ),
                dataIndex: '',
                width: '180px',
                editable: false,
                render: (a: any) => {
                    const rndPercentage = parseFloat(a.rndAllocationPercentage) || 0;
                    const netAmount = parseFloat(a.netAmount) || 0;
                    const rndTotal = (rndPercentage / 100) * netAmount;
                    const adjustmentsPercentage = a.isConnected ? 100 : 65;
                    const total = (adjustmentsPercentage / 100) * rndTotal;

                    return (
                        <Text>
                            {`£ ${total.toFixed(2)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        </Text>
                    );
                }
            },
            {
                title: (
                    <PrimaryTooltip
                        title="Is Direct Cost?"
                        text="Is Direct Cost?"
                    />
                ),
                dataIndex: 'isDirectCost',
                width: '150px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="Link Projects"
                        text="Link Projects"
                    />
                ),
                dataIndex: '',
                width: '150px',
                render: (a: any, record: any, index: number) => {
                    const rndPercentage = parseFloat(a.rndAllocationPercentage) || 0;
                    const netAmount = parseFloat(a.netAmount) || 0;
                    const rndTotal = (rndPercentage / 100) * netAmount;
                    const adjustmentsPercentage = a.isConnected ? 100 : 65;
                    const total = (adjustmentsPercentage / 100) * rndTotal;

                    return (
                        <Button
                            type="primary"
                            disabled={
                                total <= 0
                            }
                            size="small"
                            onClick={() => this.showProjectsModal(record, index)}
                        >
                            Link Projects
                        </Button>
                    )
                }
            },
        ];

        this.state = {
            dataSource: [],
            count: 0,
            fetching: false,
            total: 10,
            pagination: { page: 1, perpage: '10' },
            financialDetails: [],
            claimId: null,
            updateData: [],
            saving: false,
            externalWorkerCostTotal: {
                netAmount: 0,
                rnd: 0,
                externalWorkerAdjustment: 0,
                total: 0,
            },
            claimDetails: {},
            columns: this.columns,
            compareCount: 0,
            openLinkProjectsModal: false,
            record: null,
            index: null,
            projectsList: [],
        };
    }

    componentDidMount(): void {
        const {
            //@ts-ignore
            match: { params },
        } = this.props;
        this.setState({ claimId: params.claimId }, () => {
            this.getExternalWorkerCostList();
        });
        this.getProjects(params.claimId);
    }


    showProjectsModal = (record: any,index:number) => {
        this.setState({
            openLinkProjectsModal: true,
            record: record,
            index: index
        });
    };

    getProjects = async (claimId: string) => {
        try {
            const {
                data: { data },
            } = await API_SERVICE.getProjects(claimId);
            this.setState({ projectsList: data.items });
        } catch (e) {
            notification.error({ message: API_SERVICE.handleErrors(e) });
        }
    };


    getExternalWorkerCostList = async () => {
        this.setState({ fetching: true });
        const params = { ...this.state.pagination };
        try {
            const {
                data: { data },
            } = await API_SERVICE.externalWorkerCostList(
                this.state.claimId,
                params,
            );
            let mData = data.items.map((item: any, i: number) => {
                item.key = i;
                return item;
            });
            this.setState({
                dataSource: mData,
                count: data.items.length,
                total: data.total,
            });
            this.getExternalWorkerCostTotal();
        } catch (e) {
            notification.error({ message: API_SERVICE.handleErrors(e) });
        } finally {
            this.setState({ fetching: false });
        }
    };

    getExternalWorkerCostTotal = async () => {
        try {
            await API_SERVICE.externalWorkerCostTotal(this.state.claimId).then(
                (result) => {
                    const {
                        data: { data },
                    } = result;
                    this.setState({ externalWorkerCostTotal: data });
                    this.getClaimsDetails();
                },
            );
        } catch (e) {
            notification.error({ message: API_SERVICE.handleErrors(e) });
        }
    };

    getClaimsDetails = async () => {
        try {
            const {
                data: { data },
            } = await API_SERVICE.getClaimById(this.state.claimId);
            this.setState({ claimDetails: data }, () => {
                this.setUpdatedColumns();
            });
        } catch (e) {
            notification.error({ message: API_SERVICE.handleErrors(e) });
        }
    };

    setUpdatedColumns = () => {
        const editableSMEColumn = [
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text="SME Allocation (%)"
                    />
                ),
                dataIndex: 'smeAllocation',
                width: '200px',
                editable: true,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text="SME Allocation (GBP)"
                    />
                ),
                dataIndex: 'smeTotal',
                width: '200px',
                editable: false,
                render: (a: any) => (
                    <Text>
                        {`£ ${a ? a : 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </Text>
                ),
            },
        ];

        const nonEditableSMEColumn = [
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text="SME Allocation (%)"
                    />
                ),
                dataIndex: 'smeAllocation',
                width: '200px',
                editable: false,
            },
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text="SME Allocation (GBP)"
                    />
                ),
                dataIndex: 'smeTotal',
                width: '200px',
                editable: false,
                render: (a: any) => (
                    <Text>
                        {`£ ${a ? a : 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </Text>
                ),
            },
        ];

        const nonEditableRDECColumn = [
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text="RDEC Allocation (%)"
                    />
                ),
                key: 'rdecAllocation',
                width: '150px',
                editable: false,
                render: (a: any) => (
                    <Text>{`${
                        100 - (a.smeAllocation ? a.smeAllocation : 0)
                    } %`}</Text>
                ),
            },
            {
                title: (
                    <PrimaryTooltip
                        title="prompt text"
                        text="RDEC Allocation (GBP)"
                    />
                ),
                dataIndex: 'rdecTotal',
                width: '150px',
                editable: false,
                render: (a: any) => (
                    <Text>
                        {`£ ${a ? a : 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </Text>
                ),
            },
        ];

        if (this.state.claimDetails?.type?.id == 1) {
            let newColumns = [...this.columns, ...nonEditableRDECColumn];
            this.setState({ columns: newColumns });
        } else if (this.state.claimDetails?.type?.id == 2) {
            let newColumns = [...this.columns, ...nonEditableSMEColumn];
            this.setState({ columns: newColumns });
        } else if (this.state.claimDetails?.type?.id == 3) {
            let newColumns = [
                ...this.columns,
                ...editableSMEColumn,
                ...nonEditableRDECColumn,
            ]; // @media screen and (min-width: 780px) {
            //     .addEmployeeBox {
            //         display: flex;
            //         flex-direction: column;
            //     }
            // }

            this.setState({ columns: newColumns });
        }
    };

    /*setUpdatedColumns = () => {
        const SMEColumn = [
            {
                title: (<PrimaryTooltip title="prompt text" text='SME Allocation (%)' />),
                dataIndex: 'smeAllocation',
                width: '200px',
                editable: true
            },
            {
                title: (<PrimaryTooltip title="prompt text" text='SME Allocation (GBP)' />),
                dataIndex: 'smeAllocation',
                width: '200px',
                editable: false
            }
        ];

        const RDECColumn = [
            {
                title: (<PrimaryTooltip title="prompt text" text='RDEC Allocation (%)' />),
                dataIndex: 'rdecAllocation',
                width: '200px',
                editable: true
            },
            {
                title: (<PrimaryTooltip title="prompt text" text='RDEC Allocation (GBP)' />),
                dataIndex: 'rdecAllocation',
                width: '200px',
                editable: false
            }
        ]

        if(this.state.claimDetails?.type?.id == 1){
            let newColumns = [...this.columns, ...RDECColumn];
            this.setState({columns: newColumns});
        }
        else if(this.state.claimDetails?.type?.id == 2){
            let newColumns = [...this.columns, ...SMEColumn];
            this.setState({columns: newColumns});
        }else if(this.state.claimDetails?.type?.id == 3){
            let newColumns = [...this.columns, ...SMEColumn, ...RDECColumn];
            this.setState({columns: newColumns});
        }
    };*/

    handleDelete = (row: any) => {
        if (row.id) {
            this.handleDeleteExternalWorkerCost(row.id);
        } else {
            const dataSource = [...this.state.dataSource];
            this.setState({
                dataSource: dataSource.filter((item) => item.key !== row.key),
            });
        }
    };

    handleAdd = () => {
        const { count, dataSource } = this.state;
        //@ts-ignore
        const newData: DataType = {
            key: count,
            id: null,
            name: '',
            role: '',
            agency: '',
            netAmount: '',
            rndAllocationPercentage: '',
            isConnected: false,
            adjustmentsPercentage: 65,
            isDirectCost: null,
        };
        this.setState({
            dataSource: [...dataSource, newData],
            count: count + 1,
        });
    };

    handleSave = (row: DataType) => {
        //to add new dummy row to frontend list
        const newData = [...this.state.dataSource];
        const index = newData.findIndex((item) => row.key === item.key);
        const item = newData[index];
        newData.splice(index, 1, {
            ...item,
            ...row,
        });

        //to update the reqObj for create/update
        const updateData: Array<any> = [...this.state.updateData];
        const isExist = updateData.findIndex((item) => row.key === item.key);
        if (isExist > -1) {
            const updateItem = updateData[isExist];
            updateData.splice(isExist, 1, {
                ...updateItem,
                ...row,
            });
        } else {
            updateData.push(row);
        }

        this.setState({ dataSource: newData, updateData });
    };

    handleSaveExternalWorkerCost = async () => {
        let reqObj = {} as any;
        reqObj['costs'] = this.state.updateData;
        if (
            this.state.dataSource.length > this.state.compareCount &&
            reqObj['costs'].length === 0
        ) {
            notification.error({
                message: `Please Check & fill all the required Fields`,
            });
            return;
        }
        if (
            this.state.dataSource.length === this.state.compareCount &&
            reqObj['costs'].length === 0
        ) {
            notification.success({
                message: `Details Already Saved`,
            });
            return;
        }
        this.setState({ saving: true });
        await API_SERVICE.externalWorkerCostCreate(this.state.claimId, reqObj)
            .then((d) => {
                this.getExternalWorkerCostList();
                this.setState({ updateData: [] });
                notification.success({
                    message: 'Success',
                });
            })
            .catch((e) => {
                console.log(e);
                notification.error({ message: API_SERVICE.handleErrors(e) });
            })
            .finally(() => {
                this.setState({ saving: false });
            });
    };

    handleDeleteExternalWorkerCost = async (id: any) => {
        this.setState({ saving: true });
        API_SERVICE.externalWorkerCostDelete(this.state.claimId, id)
            .then((d) => {
                this.getExternalWorkerCostList();
                notification.success({
                    message: 'Success',
                });
            })
            .catch((e) => {
                console.log(e);
                notification.error({ message: API_SERVICE.handleErrors(e) });
            })
            .finally(() => {
                this.setState({ saving: false });
            });
    };

    render() {
        const { dataSource } = this.state;
        const components = {
            body: {
                row: EditableRow,
                cell: (props: EditableCellProps) => (
                    <EditableCell
                        {...props}
                        claimDetails={this.state.claimDetails}
                    />
                ),

            },
        };
        //@ts-ignore
        const columns = this.state.columns.map((col) => {
            if (!col.editable) {
                return col;
            }
            return {
                ...col,
                onCell: (record: DataType) => ({
                    record,
                    editable: col.editable,
                    dataIndex: col.dataIndex,
                    title: col.title,
                    handleSave: this.handleSave,
                    inputType:
                        col.dataIndex === 'isConnected' || 
                        col.dataIndex === 'isDirectCost'
                            ? 'radio'
                            : col.dataIndex === 'netAmount' ||
                              col.dataIndex === 'total'
                            ? 'number'
                            : col.dataIndex === 'rndAllocationPercentage' ||
                              col.dataIndex === 'adjustmentsPercentage' ||
                              col.dataIndex === 'smeAllocation' ||
                              col.dataIndex === 'rdecAllocation'
                            ? 'percentage'
                            : 'text',
                }),
            };
        });
        return (
            <>
                <Row
                    className={`${
                        this.state.claimDetails.stageId >= 2 &&
                        'cursor-not-allowed'
                    }`}
                >
                    <Table
                        loading={this.state.fetching}
                        scroll={{ x: true, y: 'calc(100vh - 280px)' }}
                        components={components}
                        rowClassName={() =>
                            `${
                                this.state.claimDetails.stageId >= 2 &&
                                'pointer-none'
                            } editable-row`
                        }
                        bordered
                        dataSource={dataSource}
                        columns={columns as ColumnTypes}
                        pagination={false}
                    />
                </Row>
                <div className="d-flex align-items-center justify-content-between my-3 addEmployeeBox">
                    <Button
                        disabled={this.state.claimDetails.stageId >= 2}
                        className="black-button"
                        onClick={this.handleAdd}
                        icon={<PlusOutlined />}
                    >
                        Add External Worker
                    </Button>
                    <div className="d-flex align-items-center">
                        <Space>
                            <Form.Item
                                className="mb-0"
                                style={{ width: '220px' }}
                            >
                                <Input
                                    disabled
                                    value={`Net Amount: ${
                                        this.state.externalWorkerCostTotal
                                            .netAmount &&
                                        this.state.externalWorkerCostTotal.netAmount.toFixed(
                                            2,
                                        )
                                    } GBP`}
                                />
                            </Form.Item>
                            <Form.Item
                                className="mb-0"
                                style={{ width: '220px' }}
                            >
                                <Input
                                    disabled
                                    value={`Total: ${
                                        this.state.externalWorkerCostTotal
                                            .total &&
                                        this.state.externalWorkerCostTotal.total.toFixed(
                                            2,
                                        )
                                    } GBP`}
                                />
                            </Form.Item>
                        </Space>
                    </div>
                </div>
                <Row>
                    <Col xs={24} className={'linear justify-content-between'}>
                        <Pagination
                            showSizeChanger
                            showQuickJumper
                            defaultCurrent={1}
                            total={this.state.total}
                            onChange={(page, size) => {
                                this.setState(
                                    {
                                        pagination: {
                                            page: page,
                                            perpage: size,
                                        },
                                    },
                                    this.getExternalWorkerCostList,
                                );
                            }}
                        />

                        <Button
                            disabled={
                                this.state.claimDetails.stageId >= 2 ||
                                this.state.saving
                            }
                            loading={this.state.saving}
                            type={'primary'}
                            shape="round"
                            className="theme-button primary"
                            onClick={this.handleSaveExternalWorkerCost}
                            icon={<SaveOutlined />}
                        >
                            Save
                        </Button>
                    </Col>
                </Row>
                {this.state.openLinkProjectsModal &&
                    <CommonLinkProjects
                        displayOnly={false}
                        record={this.state.record}
                        index={this.state.index}
                        visible={this.state.openLinkProjectsModal}
                        onClose={() => this.setState({ openLinkProjectsModal: false })}
                        setState={(state: any) => this.setState(state)}
                        claimId={this.state.claimId}
                        projectsList={this.state.projectsList!}
                        handleSave={this.handleSave as unknown as (data: any) => Promise<void>}
                        type='externalWorkers'
                    />
                }
            </>
        );
    }
}

// @ts-ignore
export default withRouter(ExternalWorkers);
// @ts-ignore
// ReactDOM.render(<EditableTable />, mountNode);
